import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';

/**
 * Zugang zu den nicht-blockierenden Hinweisen (D-09) fuer die geteilten Feldkomponenten.
 *
 * Warum als Kontext und nicht als Prop: Bis Plan 20-16 reichte jedes Formular einen
 * `warning`-Prop an jede Feldkomponente durch, und getan hat das genau EINES von fuenf
 * (Unfalldaten). Die anderen vier zeigten die gelbe Stufe deshalb nie -- nicht weil dort
 * nichts anliegen koennte, sondern weil niemand sie verdrahtet hatte. Eine Feldkomponente
 * kennt ihren Feldnamen ohnehin; holt sie sich ihren Hinweis selbst, greift die Anzeige in
 * allen fuenf Formularen ohne deren Zutun und ohne handgepflegte Feldlisten.
 *
 * Der Kontexttyp ist bewusst strukturell (`get`/`subscribe`) und nicht der importierte
 * `WarningsStore` aus `components/reports/formStores.ts`: `components/form` ist die
 * allgemeinere Ebene und soll nicht auf `components/reports` zurueckzeigen. Der vorhandene
 * Store passt ohne Anpassung in diese Form.
 */
export interface FieldWarningSource {
  get(): Record<string, string>;
  subscribe(listener: () => void): () => void;
}

export const FieldWarningsContext = createContext<FieldWarningSource | null>(null);

const NOOP_SUBSCRIBE = () => () => {};

/**
 * Liest den Hinweis genau EINES Feldes.
 *
 * Der Schnappschuss ist der Meldungstext selbst, nicht das Hinweis-Objekt: `useSyncExternalStore`
 * vergleicht mit `Object.is` und rendert eine Feldkomponente nur dann neu, wenn sich IHR Text
 * geaendert hat. Ein Formular mit 280 Feldern haelt damit zwar 280 Abonnements, rendert aber
 * pro Validierungsantwort nur die tatsaechlich betroffenen Felder neu.
 */
export function useFieldWarning(field: string): string | undefined {
  const source = useContext(FieldWarningsContext);

  const getSnapshot = useCallback(
    () => (source ? source.get()[field] : undefined),
    [source, field]
  );

  return useSyncExternalStore(source ? source.subscribe : NOOP_SUBSCRIBE, getSnapshot);
}

const EMPTY_WARNINGS: Record<string, string> = {};
const getEmptyWarnings = () => EMPTY_WARNINGS;

/**
 * Liest den GESAMTEN Hinweisstand -- fuer die Reiterkennzeichnung, die wissen muss, welche
 * Reiter Hinweise tragen, nicht was an einem einzelnen Feld steht.
 *
 * Der Objekt-Schnappschuss ist hier unbedenklich, obwohl `useFieldWarning` oben aus gutem Grund
 * den blossen Text abonniert: der Store ERSETZT sein Objekt nur beim Eintreffen einer
 * Validierungsantwort, und genau dann rendert das Formular ohnehin neu (`form.setErrors` aus
 * derselben Antwort). `getSnapshot` gibt dieselbe Referenz zurueck, solange sich nichts aendert --
 * Reacts Warnung "The result of getSnapshot should be cached" greift damit nicht.
 *
 * Ausschliesslich fuer Formularrahmen gedacht, nie fuer Feldkomponenten: ein Feld, das hierueber
 * liest, rendert bei JEDER Validierungsantwort neu statt nur bei seiner eigenen.
 */
export function useAllFieldWarnings(): Record<string, string> {
  const source = useContext(FieldWarningsContext);

  return useSyncExternalStore(
    source ? source.subscribe : NOOP_SUBSCRIBE,
    source ? source.get : getEmptyWarnings
  );
}
